import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Activity, Boxes, Package, Tags, Clock, CheckCircle2, FileText, PlayCircle, XCircle,
  AlertTriangle, FileSpreadsheet, ShieldCheck, Image as ImageIcon, PieChart as PieIcon, ScrollText, Percent,
  Layers, Users as UsersIcon, RefreshCw, HelpCircle, TrendingUp, Download, FileText as FileTextIcon
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, Legend
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHART_COLORS = [
  "hsl(173 80% 40%)",
  "hsl(199 88% 48%)",
  "hsl(38 92% 55%)",
  "hsl(142 70% 42%)",
  "hsl(0 78% 52%)",
];

function cov_class(pct) {
  const v = pct ?? 0;
  if (v >= 80) return "text-emerald-400";
  if (v >= 40) return "text-amber-400";
  return "text-destructive";
}

/**
 * Infer a visual style (icon + color tone) for a status card based on its name.
 * Keeps the UI consistent even when the admin renames or adds new statuses.
 */
function styleForStatus(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("pend")) return { icon: Clock, color: "warning" };
  if (n.includes("proc") || n.includes("progreso") || n.includes("curso"))
    return { icon: PlayCircle, color: "accent" };
  if (n.includes("complet") || n.includes("termin") || n.includes("final") || n.includes("cerr"))
    return { icon: CheckCircle2, color: "success" };
  if (n.includes("cancel") || n.includes("rechaz")) return { icon: XCircle, color: "danger" };
  if (n.includes("espera") || n.includes("pause") || n.includes("paus"))
    return { icon: Clock, color: "warning" };
  return { icon: FileText, color: "primary" };
}

function ChartCard({ title, description, children, testId }) {
  return (
    <Card className="bg-card border-border" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold tracking-tight">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function TooltipDark({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-3 py-2 text-xs shadow-lg">
      <div className="text-muted-foreground mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-foreground font-mono">{p.name}: {p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const [range, setRange] = useState({});
  const [clienteFilter, setClienteFilter] = useState("all");
  const qc = useQueryClient();

  const params = {};
  if (range.from) params.date_from = format(range.from, "yyyy-MM-dd");
  if (range.to) params.date_to = format(range.to, "yyyy-MM-dd");
  if (clienteFilter !== "all") params.cliente = clienteFilter;

  const { data: stats, isLoading, isFetching, refetch: refetchStats } = useQuery({
    queryKey: ["dashboard-stats", params.date_from || "all", params.date_to || "all", clienteFilter],
    queryFn: async () => (await api.get("/dashboard/stats", { params })).data,
    refetchInterval: 120000,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Fetch clientes catalog for the filter dropdown
  const { data: clientesCatalog = [] } = useQuery({
    queryKey: ["catalogs", "cliente"],
    queryFn: async () => (await api.get("/catalogs", { params: { type: "cliente" } })).data,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: recent, refetch: refetchRecent } = useQuery({
    queryKey: ["dashboard-recent"],
    queryFn: async () => (await api.get("/dashboard/recent", { params: { limit: 8 } })).data,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const forceRefresh = async () => {
    // Invalidate every dashboard-related query so any downstream change
    // (catalog rename, new OT, evidencia upload, etc.) is picked up.
    await qc.invalidateQueries({ queryKey: ["catalogs"] });
    await Promise.all([refetchStats(), refetchRecent()]);
    toast.success("Dashboard actualizado");
  };

  const [downloadingReport, setDownloadingReport] = useState(false);
  const downloadFaltantesReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await api.get("/dashboard/faltantes-report", { params, responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateSuffix = [params.date_from, params.date_to].filter(Boolean).join("_");
      a.href = url;
      a.download = dateSuffix ? `reporte_faltantes_${dateSuffix}.xlsx` : "reporte_faltantes.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte de faltantes descargado");
    } catch (e) {
      toast.error("Error al descargar el reporte");
    } finally {
      setDownloadingReport(false);
    }
  };

  const [downloadingExec, setDownloadingExec] = useState(false);
  const downloadExecutiveReport = async (format) => {
    setDownloadingExec(true);
    try {
      const path = format === "pdf" ? "/dashboard/report/pdf" : "/dashboard/report/excel";
      const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const res = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = [params.date_from, params.date_to].filter(Boolean).join("_");
      const ext = format === "pdf" ? "pdf" : "xlsx";
      a.href = url;
      a.download = suffix ? `reporte_ejecutivo_${suffix}.${ext}` : `reporte_ejecutivo.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`Reporte ejecutivo ${format.toUpperCase()} descargado`);
    } catch (e) {
      toast.error("Error al generar el reporte ejecutivo");
    } finally {
      setDownloadingExec(false);
    }
  };

  if (isLoading && !stats) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between">
          <div>
            <Skeleton className="h-6 w-32 mb-1" />
            <Skeleton className="h-4 w-56" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-40" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const t = stats?.totals || {};
  const e = stats?.evidencia || {};
  const clas = stats?.clasificaciones || {};
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground">Resumen de operación y métricas clave.</p>
          </div>
          {isFetching && !isLoading && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground animate-pulse">
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span>Actualizando...</span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
          <DateRangeFilter value={range} onChange={setRange} />
          <Select value={clienteFilter} onValueChange={setClienteFilter}>
            <SelectTrigger className="h-10 sm:h-9 w-full sm:w-52" data-testid="dashboard-cliente-filter">
              <UsersIcon className="h-3.5 w-3.5 mr-1.5 text-primary shrink-0" />
              <SelectValue placeholder="Todos los clientes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los clientes</SelectItem>
              {clientesCatalog.map((c) => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-3 sm:flex sm:items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={forceRefresh}
              disabled={isFetching}
              data-testid="dashboard-refresh-button"
              className="h-10 sm:h-9 col-span-3 sm:col-span-1"
              title="Actualizar KPIs, gráficas y catálogos"
            >
              <RefreshCw className={`h-4 w-4 mr-2 text-primary ${isFetching ? "animate-spin" : ""}`} />
              <span className="truncate">{isFetching ? "Actualizando..." : "Actualizar"}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  disabled={downloadingExec}
                  data-testid="dashboard-download-report-button"
                  className="h-10 sm:h-9 col-span-3 sm:col-span-1"
                  title="Descargar reporte ejecutivo (Excel o PDF)"
                >
                  <Download className={`h-4 w-4 mr-2 ${downloadingExec ? "animate-pulse" : ""}`} />
                  <span className="truncate">{downloadingExec ? "Generando..." : "Descargar reporte"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Formato del reporte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => downloadExecutiveReport("excel")}
                  data-testid="dashboard-download-report-excel"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-400" />
                  <span>Excel (multi-hoja)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadExecutiveReport("pdf")}
                  data-testid="dashboard-download-report-pdf"
                >
                  <FileTextIcon className="h-4 w-4 mr-2 text-rose-400" />
                  <span>PDF ejecutivo</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              size="sm"
              onClick={downloadFaltantesReport}
              disabled={downloadingReport}
              data-testid="dashboard-download-faltantes-report-button"
              className="h-10 sm:h-9 col-span-3 sm:col-span-1"
              title="Descargar reporte Excel de faltantes"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-400" />
              <span className="truncate">{downloadingReport ? "Generando..." : "Reporte Faltantes"}</span>
            </Button>
            <Link to="/ots/new" className="col-span-2 sm:col-span-1"><Button data-testid="dashboard-new-ot-button" size="sm" className="w-full h-10 sm:h-9">Nueva OT</Button></Link>
            <Link to="/ots" className="col-span-1 sm:col-span-1"><Button variant="secondary" size="sm" className="w-full h-10 sm:h-9">Ver todas</Button></Link>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
        <KpiCard label="Total OTs" value={t.total_ots ?? 0} icon={FileText} testId="kpi-total-ots" color="primary" />
        <KpiCard label="Piezas Procesadas" value={(t.total_pz ?? 0).toLocaleString()} icon={Package} testId="kpi-total-pz" color="accent" />
        <KpiCard label="SKUs Únicos" value={t.unique_skus ?? 0} icon={Boxes} testId="kpi-unique-skus" color="success" />
        <KpiCard label="VPN Únicos" value={t.unique_vpns ?? 0} icon={Tags} testId="kpi-unique-vpns" color="warning" />
        <KpiCard
          label="Total Faltante"
          value={(t.total_faltante ?? 0).toLocaleString()}
          icon={AlertTriangle}
          testId="kpi-total-faltante"
          color={(t.total_faltante ?? 0) > 0 ? "danger" : "primary"}
          sub={(t.ots_con_faltante ?? 0) > 0 ? `${t.ots_con_faltante} OTs afectadas` : "Sin faltantes"}
        />
        <KpiCard
          label="Total Sobrante"
          value={(t.total_sobrante ?? 0).toLocaleString()}
          icon={AlertTriangle}
          testId="kpi-total-sobrante"
          color={(t.total_sobrante ?? 0) > 0 ? "warning" : "primary"}
          sub={(t.ots_con_sobrante ?? 0) > 0 ? `${t.ots_con_sobrante} OTs afectadas` : "Sin sobrantes"}
        />
      </div>

      {/* Status row - DYNAMIC: reflects any status defined in the catalog */}
      {(stats?.status_breakdown || []).length > 0 && (
        <div
          className={`grid gap-3 grid-cols-2 ${
            (stats?.status_breakdown || []).length >= 5
              ? "xl:grid-cols-5"
              : "xl:grid-cols-4"
          }`}
          data-testid="dashboard-status-kpi-row"
        >
          {(stats?.status_breakdown || []).map((s) => {
            const { icon: Icon, color } = styleForStatus(s.estatus);
            const slug = s.estatus.toLowerCase().replace(/\s+/g, "-");
            return (
              <KpiCard
                key={s.estatus}
                label={s.estatus}
                value={s.count}
                icon={Icon}
                color={color}
                testId={`kpi-status-${slug}`}
              />
            );
          })}
        </div>
      )}

      {/* Evidencias section header */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold tracking-tight uppercase">Evidencias</h2>
            <p className="text-[11px] text-muted-foreground">Cobertura, estatus y clientes pendientes.</p>
          </div>
        </div>
      </div>

      {/* Evidencias KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard
          label="Archivos subidos"
          value={(e.total_archivos ?? 0).toLocaleString()}
          icon={ImageIcon}
          testId="kpi-evidencia-archivos"
          color="primary"
          sub={`${e.skus_con_evidencia ?? 0} de ${e.total_skus ?? 0} SKUs cubiertos`}
        />
        <KpiCard
          label="Cobertura Global"
          value={`${e.cobertura ?? 0}%`}
          icon={Percent}
          testId="kpi-evidencia-cobertura"
          color={(e.cobertura ?? 0) >= 80 ? "success" : (e.cobertura ?? 0) >= 40 ? "warning" : "danger"}
          sub={
            (e.cobertura ?? 0) === 100
              ? "Cobertura total"
              : (e.cobertura ?? 0) === 0
                ? "Sin evidencias"
                : "Cobertura parcial"
          }
        />
        <KpiCard
          label="Pendientes / Parcial"
          value={(e.pendientes_o_parciales ?? 0).toLocaleString()}
          icon={AlertTriangle}
          testId="kpi-evidencia-pendientes"
          color={(e.pendientes_o_parciales ?? 0) > 0 ? "warning" : "success"}
          sub={(e.pendientes_o_parciales ?? 0) > 0 ? "OTs por atender" : "Todo al día"}
        />
        <KpiCard
          label="En Revisión"
          value={(e.en_revision ?? 0).toLocaleString()}
          icon={ScrollText}
          testId="kpi-evidencia-revision"
          color="accent"
          sub={(e.aprobadas ?? 0) + " aprobadas · " + (e.rechazadas ?? 0) + " rechazadas"}
        />
      </div>

      {/* Evidencias charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <ChartCard
          title="Distribución por Estatus de Evidencia"
          description="OTs por cada estatus de evidencia (respeta filtro de fechas)"
          testId="chart-evidencia-distribution"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats?.evidencia_by_status || []} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis dataKey="estatus" type="category" width={140} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} cursor={{ fill: "hsl(var(--muted)/0.4)" }} />
              <Bar dataKey="count" name="OTs" radius={[0, 4, 4, 0]}>
                {(stats?.evidencia_by_status || []).map((entry, idx) => {
                  const colorMap = {
                    "Pendiente de subir": "hsl(38 92% 55%)",
                    "Subida parcial": "hsl(38 92% 45%)",
                    "Subida completa": "hsl(199 88% 48%)",
                    "En revisión": "hsl(268 75% 60%)",
                    "Aprobada": "hsl(142 70% 42%)",
                    "Rechazada": "hsl(0 78% 52%)",
                    "No requiere evidencia": "hsl(142 70% 52%)",
                  };
                  return <Cell key={idx} fill={colorMap[entry.estatus] || CHART_COLORS[idx % CHART_COLORS.length]} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <Card className="bg-card border-border xl:col-span-2" data-testid="chart-top-clientes-evidencias-pendientes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Top 5 Clientes con Evidencias Pendientes
            </CardTitle>
            <CardDescription className="text-[11px]">OTs en estatus &quot;Pendiente de subir&quot;, &quot;Subida parcial&quot; o &quot;Rechazada&quot;.</CardDescription>
          </CardHeader>
          <CardContent>
            {(stats?.top_clientes_evidencias_pendientes || []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400 opacity-60" />
                Sin clientes con evidencias pendientes en este periodo.
              </div>
            ) : (
              <div className="space-y-2">
                {(stats?.top_clientes_evidencias_pendientes || []).map((c, i) => {
                  const maxCount = Math.max(...(stats?.top_clientes_evidencias_pendientes || []).map((x) => x.count));
                  const pct = maxCount > 0 ? Math.round((c.count / maxCount) * 100) : 0;
                  return (
                    <div key={i} className="flex items-center gap-3" data-testid={`evidencia-pendiente-cliente-${i}`}>
                      <span className="text-[10px] font-mono text-muted-foreground w-5">#{i + 1}</span>
                      <span className="text-sm flex-1 truncate" title={c.cliente}>{c.cliente}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="font-mono font-semibold text-sm tabular-nums text-amber-400 min-w-[2.5rem] text-right">{c.count}</span>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">OTs</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ESI section header + KPIs */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold tracking-tight uppercase">Estatus ESI</h2>
            <p className="text-[11px] text-muted-foreground">
              Revisión ESI y envío de reporte al cliente. {clienteFilter !== "all" ? `Filtrado por ${clienteFilter}.` : "Vista global."}
            </p>
          </div>
        </div>
      </div>

      {/* ESI KPI row - DYNAMIC: one card per estatus_esi from the catalog */}
      {(stats?.esi_by_status || []).length > 0 && (
        <div
          className={`grid gap-3 grid-cols-2 ${
            (stats?.esi_by_status || []).length >= 5 ? "xl:grid-cols-5" : "xl:grid-cols-4"
          }`}
          data-testid="dashboard-esi-kpi-row"
        >
          {(stats?.esi_by_status || []).map((s) => {
            const { icon: Icon, color } = styleForStatus(s.estatus);
            const slug = s.estatus.toLowerCase().replace(/\s+/g, "-");
            return (
              <KpiCard
                key={s.estatus}
                label={s.estatus}
                value={s.count}
                icon={Icon}
                color={color}
                testId={`kpi-esi-${slug}`}
              />
            );
          })}
        </div>
      )}

      {/* Clasificaciones section header */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
            <Layers className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold tracking-tight uppercase">Clasificaciones</h2>
            <p className="text-[11px] text-muted-foreground">
              Conteo de piezas por Clas. B, C y D. {clienteFilter !== "all" ? `Filtrado por ${clienteFilter}.` : "Vista global."}
            </p>
          </div>
        </div>
      </div>

      {/* Simple KPI row: focus on damaged pieces (C + D). Clas. B (buen estado) muted as reference. */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard
          label="PZ Buen Estado (Clas. B)"
          value={(clas.B?.pz_clasificadas ?? 0).toLocaleString()}
          icon={CheckCircle2}
          testId="kpi-clas-b-pz"
          color="success"
          sub={`${clas.B?.items_clasificados ?? 0} partidas${clas.B?.top_valor ? ` · más común: ${clas.B.top_valor}` : ""}`}
        />
        <KpiCard
          label="PZ Dañadas Clas. C"
          value={(clas.C?.pz_clasificadas ?? 0).toLocaleString()}
          icon={AlertTriangle}
          testId="kpi-clas-c-pz"
          color="warning"
          sub={`${clas.C?.items_clasificados ?? 0} partidas${clas.C?.top_valor ? ` · más común: ${clas.C.top_valor}` : ""}`}
        />
        <KpiCard
          label="PZ Dañadas Clas. D"
          value={(clas.D?.pz_clasificadas ?? 0).toLocaleString()}
          icon={XCircle}
          testId="kpi-clas-d-pz"
          color="danger"
          sub={`${clas.D?.items_clasificados ?? 0} partidas${clas.D?.top_valor ? ` · más común: ${clas.D.top_valor}` : ""}`}
        />
        <KpiCard
          label="Total Dañadas (C + D)"
          value={(stats?.piezas_danadas?.total_pz ?? 0).toLocaleString()}
          icon={AlertTriangle}
          testId="kpi-piezas-danadas-total"
          color="danger"
          sub={
            (stats?.piezas_danadas?.total_pz ?? 0) > 0
              ? `${stats?.piezas_danadas?.total_items ?? 0} partidas · ${stats?.piezas_danadas?.pct_del_total ?? 0}% del total`
              : "Sin piezas dañadas 🎉"
          }
        />
      </div>

      {/* Top Pedimentos con Piezas Dañadas + Top valores tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <Card className="bg-card border-border" data-testid="card-top-pedimentos-danadas">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Top Pedimentos con más PZ Dañadas
            </CardTitle>
            <CardDescription className="text-[11px]">
              Piezas dañadas = suma de Clas. C + Clas. D (una partida solo cuenta una vez aunque tenga ambas).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(stats?.piezas_danadas?.top_pedimentos || []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400 opacity-60" />
                Sin piezas dañadas en este periodo.
              </div>
            ) : (
              <div className="space-y-2.5">
                {(stats?.piezas_danadas?.top_pedimentos || []).map((p, i) => {
                  const maxPz = Math.max(
                    ...(stats?.piezas_danadas?.top_pedimentos || []).map((x) => x.pz)
                  );
                  const pct = maxPz > 0 ? Math.round((p.pz / maxPz) * 100) : 0;
                  return (
                    <div
                      key={i}
                      className="space-y-1"
                      data-testid={`pedimento-danadas-row-${i}`}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-medium truncate flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-muted-foreground shrink-0">#{i + 1}</span>
                          <span className="truncate" title={p.pedimento}>{p.pedimento}</span>
                          {p.cliente && (
                            <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                              · {p.cliente}
                            </span>
                          )}
                        </span>
                        <span className="font-mono font-semibold tabular-nums text-destructive whitespace-nowrap ml-2">
                          {p.pz.toLocaleString()}
                          <span className="text-[10px] text-muted-foreground ml-1">PZ</span>
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-destructive/70 to-destructive transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {p.items} {p.items === 1 ? "partida" : "partidas"} afectadas
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top SKUs with most damaged PZ (C ∪ D). Breakdown per C vs D. */}
        <Card className="bg-card border-border" data-testid="card-top-skus-danadas">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4 text-destructive" />
              Top SKUs con más PZ Dañadas
            </CardTitle>
            <CardDescription className="text-[11px]">
              Los 5 SKUs con más piezas en Clasificaciones C o D. Muestra desglose por severidad.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(stats?.piezas_danadas?.top_skus || []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400 opacity-60" />
                Sin SKUs con piezas dañadas en este periodo.
              </div>
            ) : (
              <div className="space-y-2.5">
                {(stats?.piezas_danadas?.top_skus || []).map((s, i) => {
                  const maxPz = Math.max(
                    ...(stats?.piezas_danadas?.top_skus || []).map((x) => x.pz)
                  );
                  const totalPz = s.pz || 1;
                  const pctC = Math.round((s.pz_c / totalPz) * 100);
                  const pctD = 100 - pctC;
                  const widthOfBar = maxPz > 0 ? Math.round((s.pz / maxPz) * 100) : 0;
                  return (
                    <div
                      key={i}
                      className="space-y-1"
                      data-testid={`sku-danadas-row-${i}`}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-medium truncate flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-muted-foreground shrink-0">#{i + 1}</span>
                          <span className="truncate" title={s.sku}>{s.sku}</span>
                          {s.cliente && (
                            <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                              · {s.cliente}
                            </span>
                          )}
                        </span>
                        <span className="font-mono font-semibold tabular-nums text-destructive whitespace-nowrap ml-2">
                          {s.pz.toLocaleString()}
                          <span className="text-[10px] text-muted-foreground ml-1">PZ</span>
                        </span>
                      </div>
                      {/* Split bar showing C vs D contribution */}
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden flex" style={{ width: `${widthOfBar}%`, minWidth: "4px" }}>
                        {s.pz_c > 0 && (
                          <div
                            className="h-full bg-amber-500"
                            style={{ width: `${pctC}%` }}
                            title={`Clas. C: ${s.pz_c.toLocaleString()} PZ`}
                          />
                        )}
                        {s.pz_d > 0 && (
                          <div
                            className="h-full bg-destructive"
                            style={{ width: `${pctD}%` }}
                            title={`Clas. D: ${s.pz_d.toLocaleString()} PZ`}
                          />
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex gap-3">
                        {s.pz_c > 0 && (
                          <span className="text-amber-400">
                            <span className="inline-block h-2 w-2 rounded-sm bg-amber-500 mr-1 align-middle" />
                            C: {s.pz_c.toLocaleString()} PZ
                          </span>
                        )}
                        {s.pz_d > 0 && (
                          <span className="text-destructive">
                            <span className="inline-block h-2 w-2 rounded-sm bg-destructive mr-1 align-middle" />
                            D: {s.pz_d.toLocaleString()} PZ
                          </span>
                        )}
                        <span className="ml-auto">{s.items} {s.items === 1 ? "partida" : "partidas"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <ChartCard title="Top 5 Clientes" description="OTs por cliente" testId="chart-top-clientes">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats?.top_clientes || []} layout="vertical" margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis dataKey="cliente" type="category" width={110} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} cursor={{ fill: "hsl(var(--muted)/0.4)" }} />
              <Bar dataKey="count" name="OTs" fill="hsl(173 80% 40%)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Puertos de Entrada" description="Top puertos más utilizados" testId="chart-puertos">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={(stats?.top_puertos || []).map((p) => ({ name: p.puerto, value: p.count }))}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={45}
                paddingAngle={3}
              >
                {(stats?.top_puertos || []).map((_, idx) => (
                  <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} stroke="hsl(var(--card))" />
                ))}
              </Pie>
              <Tooltip content={<TooltipDark />} />
              <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Estatus de OTs" description="Distribución por estatus" testId="chart-status-breakdown">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats?.status_breakdown || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="estatus" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} cursor={{ fill: "hsl(var(--muted)/0.4)" }} />
              <Bar dataKey="count" name="OTs" radius={[4, 4, 0, 0]}>
                {(stats?.status_breakdown || []).map((_, idx) => (
                  <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <ChartCard title="Tendencia OTs (12 meses)" description="OTs creadas por mes" testId="chart-ots-month">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats?.ots_by_month || []}>
              <defs>
                <linearGradient id="areaMonth" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(173 80% 40%)" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(173 80% 40%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} />
              <Area type="monotone" dataKey="count" stroke="hsl(173 80% 40%)" fill="url(#areaMonth)" strokeWidth={2} name="OTs" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="OTs por Semana" description="Últimas 12 semanas" testId="chart-ots-week">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats?.ots_by_week || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="week" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} cursor={{ fill: "hsl(var(--muted)/0.3)" }} />
              <Bar dataKey="count" name="OTs" fill="hsl(199 88% 48%)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Tipos de Trabajo" description="Más solicitados" testId="chart-tipos">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats?.top_tipos || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis dataKey="tipo" type="category" width={100} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<TooltipDark />} cursor={{ fill: "hsl(var(--muted)/0.3)" }} />
              <Bar dataKey="count" name="OTs" fill="hsl(38 92% 55%)" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Recent OTs */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold tracking-tight flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" /> Últimas OTs
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-y border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3">Folio</th>
                  <th className="py-2 px-3">Cliente</th>
                  <th className="py-2 px-3">Fecha</th>
                  <th className="py-2 px-3 text-right">PZ</th>
                  <th className="py-2 px-3 text-right">SKUs</th>
                  <th className="py-2 px-3">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {(recent || []).map((o) => (
                  <tr key={o.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="py-2 px-3 font-mono">
                      <Link to={`/ots/${o.id}`} className="text-primary hover:underline">{o.folio}</Link>
                    </td>
                    <td className="py-2 px-3">{o.cliente}</td>
                    <td className="py-2 px-3 text-muted-foreground">{(o.created_at || "").substring(0, 10)}</td>
                    <td className="py-2 px-3 text-right font-mono">{o.total_pz}</td>
                    <td className="py-2 px-3 text-right font-mono">{o.unique_skus}</td>
                    <td className="py-2 px-3"><StatusBadge status={o.estatus} /></td>
                  </tr>
                ))}
                {(recent || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No hay OTs registradas todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
